import { ActionIcon, Badge, Button, Modal, Text, TextInput, useComputedColorScheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconBulb, IconDeviceFloppy, IconMaximize, IconMinimize, IconX } from '@tabler/icons-react';
import type { OnMount } from '@monaco-editor/react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExecutorVar } from '~/common/vm/protocol';
import { addAllLibTypes, addVarsType, setupTypescript } from '~/logic/monaco/setup-typescript';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import { CodeGuide } from './CodeGuide';

const Editor = dynamic(() => import('@monaco-editor/react'), { ssr: false });
type MonacoEditor = Parameters<OnMount>[0];

// Prettier is a large library, loading dynamically for code splitting
async function formatCode(code: string) {
  const [{ default: prettier }, { default: babelPlugin }, { default: esTreePlugin }] = await Promise.all([
    import('prettier/standalone'),
    import('prettier/plugins/babel'),
    import('prettier/plugins/estree'),
  ]);
  return prettier.format(code, {
    parser: 'babel',
    plugins: [babelPlugin, esTreePlugin],
    useTabs: false,
    semi: true,
    singleQuote: true,
  });
}

// Note: We use a modal so that we can control the closeOnClickOutside/closeOnEscape dynamically
export function EditCodeModal({
  testContentId,
  readOnly,
  initialCode,
  initialDescription,
  showDescription,
  onClose,
  opened,
  onSave,
  height,
  variables,
  stepIdx,
  step,
}: {
  testContentId: string;
  readOnly?: boolean;
  initialCode: string;
  initialDescription?: string;
  showDescription: boolean;
  opened: boolean;
  onClose: () => void;
  onSave: (code: string, description?: string) => Promise<void>;
  height: string;
  variables?: ExecutorVar[];
  stepIdx: number;
  step: SelectedStep;
}) {
  const { data: testContent } = api.testContent.get.useQuery({ id: testContentId }, { refetchOnMount: false });
  const colorScheme = useComputedColorScheme();
  const [code, setCode] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [formattedSavedCode, setFormattedSavedCode] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState(initialDescription);
  const editorRef = useRef<MonacoEditor | null>(null);
  const descriptionRef = useRef(initialDescription);
  const isSavingRef = useRef(false);

  const [codeWithError, setCodeWithError] = useState<string | undefined>(undefined);
  const hasFormattingError = code === codeWithError;

  const isSaved = code === formattedSavedCode && description === initialDescription;

  // Keep a ref in sync so the Monaco Ctrl/Cmd+S action always uses the latest description value.
  useEffect(() => {
    descriptionRef.current = description;
  }, [description]);

  // Reset local description state when the modal opens or the upstream description changes.
  // This avoids carrying stale text across reopen cycles.
  useEffect(() => {
    setDescription(initialDescription);
    descriptionRef.current = initialDescription;
  }, [initialDescription, opened]);

  useEffect(() => {
    if (!initialCode || !opened) {
      return;
    }
    formatCode(initialCode)
      .then((formattedCode) => {
        setFormattedSavedCode(formattedCode);
        setCode(formattedCode);
        editorRef.current?.setValue(formattedCode);
      })
      .catch(() => undefined);
  }, [initialCode, opened]);

  // TODO: add better code validation
  const saveHandler = useCallback(
    async (codeToSave: string, description: string | undefined) => {
      if (isSavingRef.current) {
        return;
      }
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        const newFormattedSavedCode = await formatCode(codeToSave).catch(() => {
          setCodeWithError(codeToSave);
          return undefined;
        });
        if (!newFormattedSavedCode) {
          return;
        }
        const didSave = await onSave(newFormattedSavedCode, description)
          .then(() => true)
          .catch(() => {
            notifications.show({ message: 'Error saving code', color: 'red' });
            return false;
          });
        if (!didSave) {
          return;
        }
        onClose();
        notifications.show({
          color: 'teal',
          message: 'Code Updated',
          icon: <IconDeviceFloppy size="1rem" />,
        });
        setFormattedSavedCode(newFormattedSavedCode);
        setCode(newFormattedSavedCode);
        editorRef.current?.setValue(newFormattedSavedCode);
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    },
    [onClose, onSave],
  );

  const closeHandler = () => {
    if (!isSaved) {
      modals.openConfirmModal({
        zIndex: 311,
        centered: true,
        title: 'Unsaved changes',
        children: <Text>Are you sure you want to close? All unsaved changes will be lost.</Text>,
        confirmProps: { color: 'red' },
        labels: { confirm: 'Yes, close', cancel: 'No, keep editing' },
        onCancel: () => {
          // Do nothing, keep the modal open
        },
        onConfirm: () => {
          onClose();
          setFullscreen(false);
        },
      });
    } else {
      onClose();
      setFullscreen(false);
    }
  };

  return (
    <Modal.Root
      opened={opened}
      onClose={() => {
        // If the modal is fullscreen, first 'esc' will exit fullscreen, second 'esc' will close the modal
        // Close Button will close the modal immediately, skipping this logic
        if (fullscreen) {
          setFullscreen(false);
        } else {
          closeHandler();
        }
      }}
      fullScreen={fullscreen}
      size="100%"
      centered
      shadow="lg"
    >
      <Modal.Overlay
        opacity={0.5}
        zIndex={301}
      />
      <Modal.Content style={{ zIndex: 310 }}>
        <Modal.Header>
          <Modal.Title className="w-full">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-1">
                <Text className="mt-2 px-2 text-lg font-medium">{testContent?.test?.name}</Text>
                <div className="flex items-center gap-2 px-2">
                  <Text className="font-medium text-gray-600 dark:text-gray-400">Step {stepIdx + 1}:</Text>
                  <Text className="font-medium">{step?.description}</Text>
                  {!readOnly &&
                    (isSaved ?
                      <Badge
                        color="green"
                        size="sm"
                        variant="light"
                      >
                        saved
                      </Badge>
                    : <Badge
                        color="yellow"
                        size="sm"
                        variant="light"
                      >
                        unsaved changes
                      </Badge>)}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Text className="font-medium text-gray-600 dark:text-gray-400">JavaScript</Text>
                <Button
                  variant="subtle"
                  leftSection={<IconBulb size="0.9rem" />}
                  onClick={() => {
                    modals.open({
                      title: 'Custom Code Guide',
                      size: 'md',
                      centered: true,
                      children: <CodeGuide />,
                      zIndex: 312,
                    });
                  }}
                >
                  Guide
                </Button>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={() => setFullscreen((prev) => !prev)}
                >
                  {fullscreen ?
                    <IconMinimize size="1rem" />
                  : <IconMaximize size="0.9rem" />}
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={closeHandler}
                >
                  <IconX size="1rem" />
                </ActionIcon>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            {!!showDescription && (
              <TextInput
                placeholder="Description for your code"
                onChange={(e) => setDescription(e.target.value)}
                value={description || ''}
              />
            )}
            <Editor
              className={hasFormattingError ? 'rounded-sm border-[1px] border-red-600' : ''}
              height={fullscreen ? 'calc(100vh - 190px)' : `calc(${height} - 130px)`} // be sure to account for the modal height to include the "Save" button
              defaultLanguage="typescript"
              theme={colorScheme === 'dark' ? 'vs-dark' : 'light'}
              defaultValue={formattedSavedCode}
              options={{ minimap: { enabled: false }, readOnly }}
              beforeMount={async (monaco) => {
                setupTypescript(monaco);
                await addAllLibTypes(monaco, { playwrightTypeSelector: { page: true, expect: true } });
                if (variables) {
                  addVarsType(monaco, variables);
                }
              }}
              onMount={(editor, monaco) => {
                editorRef.current = editor;
                setCode(editor.getValue());
                editor.focus();
                editor.addAction({
                  id: 'save',
                  label: 'Save',
                  keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
                  contextMenuGroupId: 'file',
                  contextMenuOrder: 1.5,
                  run: (ed) => saveHandler(ed.getValue(), descriptionRef.current).catch(() => undefined),
                });
              }}
              onChange={(x) => {
                setCode(x || '');
              }}
            />
            <div className="flex items-center justify-end gap-4 px-4">
              <Button
                onClick={() => saveHandler(code, description)}
                disabled={isSaved || readOnly}
                loading={isSaving}
                leftSection={<IconDeviceFloppy size="0.9rem" />}
              >
                Save
              </Button>
              {!!hasFormattingError && <Text className="text-red-600">Invalid code, cannot format</Text>}
            </div>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
